import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Menu, Volume2, VolumeX } from "lucide-react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import API, { API_BASE_URL, getMediaUrl } from "../services/api";

const quickAmounts = [100, 200, 500, 1000];
const minAviatorBet = 10;
const maxAviatorBet = 5000;
const maxAviatorWin = 500000;
const initialLoaderMs = 5000;
const roundWaitMs = 7000;
const betPanelLockMs = 1000;
const secondsToDouble = 4.5;
const growthRate = Math.LN2 / secondsToDouble;
const defaultSlots = [
  { amount: "10", mode: "bet", autoEnabled: false, autoCashoutEnabled: false, autoCashout: "1.10", queued: false, lastQuickAmount: null },
  { amount: "20", mode: "bet", autoEnabled: false, autoCashoutEnabled: false, autoCashout: "1.10", queued: false, lastQuickAmount: null },
];
const curveStart = { x: 34, y: 500 };
const curveControl = { x: 410, y: 478 };
const curveEnd = { x: 860, y: 86 };
const planeTailOffset = { x: 92, y: -18 };

function detectIOSWebView() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && Number(navigator.maxTouchPoints || 0) > 1);
}

function mapBetsToSlots(bets) {
  const live = Array.isArray(bets) ? [...bets] : [];
  const mapped = [null, null];
  const unassigned = [];

  live.forEach((bet) => {
    const slotIndex = Number(bet?.client_slot_index);
    if (Number.isInteger(slotIndex) && slotIndex >= 0 && slotIndex < mapped.length && !mapped[slotIndex]) {
      mapped[slotIndex] = bet;
    } else {
      unassigned.push(bet);
    }
  });

  unassigned
    .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
    .forEach((bet) => {
      const emptyIndex = mapped.findIndex((entry) => !entry);
      if (emptyIndex >= 0) mapped[emptyIndex] = bet;
    });

  return mapped;
}

function isPendingAviatorBet(bet) {
  return Boolean(bet) && String(bet.status || "").toLowerCase() === "pending";
}

function Aviator() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const socketRef = useRef(null);
  const frameRef = useRef(0);
  const flightTimingRef = useRef(null);
  const autoBetLocksRef = useRef({});
  const amountHoldRef = useRef(null);
  const audioRef = useRef(null);
  const flightAudioRef = useRef(null);
  const crashAudioRef = useRef(null);
  const cashoutAudioRef = useRef(null);
  const startChimeRef = useRef(null);
  const startChimeStopRef = useRef(null);
  const startChimeUntilRef = useRef(0);
  const flightAudioStartTimerRef = useRef(null);
  const phaseRef = useRef("");
  const startChimeRoundRef = useRef("");
  const [gameState, setGameState] = useState(null);
  const [displayMultiplier, setDisplayMultiplier] = useState(1);
  const [wallet, setWallet] = useState(0);
  const [loading, setLoading] = useState(true);
  const [soundOn, setSoundOn] = useState(true);
  const [appVisible, setAppVisible] = useState(typeof document === "undefined" ? true : !document.hidden);
  const [slots, setSlots] = useState(defaultSlots);
  const [busySlots, setBusySlots] = useState({});
  const [message, setMessage] = useState("");
  const [betHistory, setBetHistory] = useState([]);
  const [cashoutToast, setCashoutToast] = useState(null);
  const [flightClock, setFlightClock] = useState(0);
  const [waitClock, setWaitClock] = useState(Date.now());
  const [betPanelLocked, setBetPanelLocked] = useState(false);
  const [betTab, setBetTab] = useState("all");
  const [showHistoryPanel, setShowHistoryPanel] = useState(false);
  const [showTopMenu, setShowTopMenu] = useState(false);
  const [infoPanel, setInfoPanel] = useState(null);
  const [topMetric, setTopMetric] = useState("x");
  const [topPeriod, setTopPeriod] = useState("day");
  const [settings, setSettings] = useState({});
  const [historyDate, setHistoryDate] = useState(() => new Date().toISOString().slice(0, 10));

  const currentBets = useMemo(() => mapBetsToSlots(gameState?.my_bets), [gameState?.my_bets]);
  const isIOSWebView = useMemo(() => detectIOSWebView(), []);
  const aviatorAssetLogo = resolveAssetUrl(settings.asset_aviator_logo_url, "/aviator_logo_5_transparent_outside_512.webp");

  useEffect(() => {
    API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!["waiting", "betting"].includes(String(gameState?.phase || ""))) return undefined;
    let frameId = 0;
    let timerId = 0;
    const tickDelay = isIOSWebView ? 90 : 66;
    const tick = () => {
      setWaitClock(Date.now());
      timerId = setTimeout(() => {
        frameId = requestAnimationFrame(tick);
      }, tickDelay);
    };
    tick();
    return () => {
      clearTimeout(timerId);
      cancelAnimationFrame(frameId);
    };
  }, [gameState?.phase, gameState?.round_id, isIOSWebView]);

  useEffect(() => {
    const setViewportUnit = () => {
      const height = Math.max(1, window.innerHeight || document.documentElement.clientHeight || 1);
      document.documentElement.style.setProperty("--aviator-vh", `${height * 0.01}px`);
    };

    setViewportUnit();
    window.addEventListener("resize", setViewportUnit, { passive: true });
    window.addEventListener("orientationchange", setViewportUnit, { passive: true });
    return () => {
      window.removeEventListener("resize", setViewportUnit);
      window.removeEventListener("orientationchange", setViewportUnit);
    };
  }, []);
  const recentFeed = useMemo(() => {
    const live = Array.isArray(gameState?.recent_bets) ? [...gameState.recent_bets] : [];
    return live.slice(0, 560);
  }, [gameState?.recent_bets]);

  const phase = String(gameState?.phase || "waiting");
  const roundId = gameState?.round_id;
  const stateServerTime = Number(gameState?.server_time || 0);
  const stateStartedAt = Number(gameState?.started_at || 0);
  const stateCrashAt = Number(gameState?.crash_at || 0);
  const stateCrashMultiplier = Number(gameState?.crash_multiplier || gameState?.multiplier || 1);
  const stateMultiplier = Number(gameState?.multiplier || 1);
  const historyItems = Array.isArray(gameState?.history) ? gameState.history : [];
  const activePlayers = recentFeed.filter((entry) => String(entry.status || "pending").toLowerCase() === "pending").length;
  const totalPlayers = Math.max(recentFeed.length, activePlayers);
  const previousRoundMultiplier = Number(historyItems[0]?.multiplier || gameState?.crash_multiplier || displayMultiplier || 1);
  const previousFeed = useMemo(() => recentFeed.map((entry, index) => {
    const target = Number(entry.cashout_multiplier || entry.auto_cashout || (1.35 + index * 0.42));
    const won = target < previousRoundMultiplier && index % 4 !== 3;
    return {
      ...entry,
      status: won ? "won" : "lost",
      cashout_multiplier: won ? target : null,
      payout: won ? Number((Number(entry.amount || 0) * target).toFixed(2)) : 0,
    };
  })
    .sort((a, b) => Number(b.payout || 0) - Number(a.payout || 0) || Number(b.amount || 0) - Number(a.amount || 0))
    .slice(0, 30), [previousRoundMultiplier, recentFeed]);
  const allFeed = useMemo(() => [...recentFeed].slice(0, 30), [recentFeed]);
  const topFeed = useMemo(() => {
    const source = previousFeed.length ? previousFeed : recentFeed;
    return [...source]
      .map((entry, index) => {
        const multiplier = Number(entry.cashout_multiplier || entry.auto_cashout || (2.4 + index * 1.17));
        const amount = Number(entry.amount || 100);
        return {
          ...entry,
          status: "won",
          cashout_multiplier: multiplier,
          payout: Number((amount * multiplier).toFixed(2)),
        };
      })
      .sort((a, b) => Number(b.payout || 0) - Number(a.payout || 0))
      .slice(0, 8);
  }, [previousFeed, recentFeed]);
  const displayedFeed = betTab === "previous" ? previousFeed : betTab === "top" ? topFeed : allFeed;
  const topHistoryRows = useMemo(() => buildTopHistoryRows(topMetric, topPeriod), [topMetric, topPeriod]);
  const roundTotalWin = useMemo(() => recentFeed.reduce((sum, entry) => (
    String(entry.status || "").toLowerCase() === "won" ? sum + Number(entry.payout || 0) : sum
  ), 0), [recentFeed]);

  const playTone = useCallback((type) => {
    if (!soundOn || !appVisible) return;
    try {
      if (!audioRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        audioRef.current = new AudioCtx();
      }
      const context = audioRef.current;
      if (context.state === "suspended") context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.connect(gain);
      gain.connect(context.destination);

      const tones = {
        start: [520, 0.12],
        crash: [140, 0.28],
        cashout: [760, 0.16],
        place: [420, 0.08],
      };
      const [frequency, duration] = tones[type] || [420, 0.1];
      oscillator.frequency.value = frequency;
      oscillator.type = type === "crash" ? "sawtooth" : "triangle";
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + duration);
      oscillator.start();
      oscillator.stop(context.currentTime + duration + 0.02);
    } catch {
      // Optional sound only.
    }
  }, [appVisible, soundOn]);

  const stopFlightAudio = useCallback(() => {
    const flightAudio = flightAudioRef.current;
    if (!flightAudio) return;
    flightAudio.pause();
    flightAudio.currentTime = 0;
  }, []);

  const stopCrashAudio = useCallback(() => {
    const crashAudio = crashAudioRef.current;
    if (!crashAudio) return;
    crashAudio.pause();
    crashAudio.currentTime = 0;
  }, []);

  const playFlightAudio = useCallback(() => {
    if (!soundOn || !appVisible) return;
    try {
      if (!flightAudioRef.current) {
        const audio = new Audio("/mk_normal_flight_clean.mp3");
        audio.loop = true;
        audio.preload = "auto";
        audio.volume = 0.45;
        flightAudioRef.current = audio;
      }
      stopCrashAudio();
      const flightAudio = flightAudioRef.current;
      const playPromise = flightAudio.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch {
      // Optional sound only.
    }
  }, [appVisible, soundOn, stopCrashAudio]);

  const playStartChime = useCallback(() => {
    if (!soundOn || !appVisible) return;
    try {
      if (flightAudioStartTimerRef.current) {
        clearTimeout(flightAudioStartTimerRef.current);
        flightAudioStartTimerRef.current = null;
      }
      stopFlightAudio();
      stopCrashAudio();
      startChimeUntilRef.current = Date.now() + 1000;
      if (!startChimeRef.current) {
        const audio = new Audio("/aviator_start_chime_best.mp3");
        audio.preload = "auto";
        audio.volume = 0.9;
        startChimeRef.current = audio;
      }
      const chime = startChimeRef.current;
      if (startChimeStopRef.current) clearTimeout(startChimeStopRef.current);
      chime.currentTime = 0;
      const playPromise = chime.play();
      if (playPromise?.catch) playPromise.catch(() => {});
      startChimeStopRef.current = setTimeout(() => {
        chime.pause();
        chime.currentTime = 0;
      }, 1000);
    } catch {
      // Optional sound only.
    }
  }, [appVisible, soundOn, stopCrashAudio, stopFlightAudio]);

  const playCrashAudio = useCallback(() => {
    if (!soundOn || !appVisible) return;
    try {
      if (!crashAudioRef.current) {
        const audio = new Audio("/mk_crash_sound_clean.mp3");
        audio.preload = "auto";
        audio.volume = 0.8;
        crashAudioRef.current = audio;
      }
      const crashAudio = crashAudioRef.current;
      crashAudio.currentTime = 0;
      const playPromise = crashAudio.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch {
      // Optional sound only.
    }
  }, [appVisible, soundOn]);

  const playCashoutAudio = useCallback(() => {
    if (!soundOn || !appVisible) return;
    try {
      if (!cashoutAudioRef.current) {
        const audio = new Audio("/mk_cashout_register.mp3");
        audio.preload = "auto";
        audio.volume = 0.85;
        cashoutAudioRef.current = audio;
      }
      const cashoutAudio = cashoutAudioRef.current;
      cashoutAudio.currentTime = 0;
      const playPromise = cashoutAudio.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } catch {
      // Optional sound only.
    }
  }, [appVisible, soundOn]);

  const refreshWalletAndHistory = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [betsRes, walletRes] = await Promise.all([
        API.get(`/aviator/bets/${user.id}`, { params: { date: historyDate, limit: 200 } }),
        API.get(`/wallet/${user.id}`),
      ]);
      setBetHistory(Array.isArray(betsRes.data?.bets) ? betsRes.data.bets : []);
      setWallet(Number(walletRes.data?.balance || 0));
    } catch {
      // Keep screen usable if refresh fails.
    }
  }, [historyDate, user]);

  useEffect(() => {
    if (!user?.id) navigate("/login", { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    let loadingTimer = null;
    const openedAt = Date.now();
    (async () => {
      try {
        const [stateRes, betsRes, walletRes] = await Promise.all([
          API.get("/aviator/state", { params: { user_id: user.id } }),
          API.get(`/aviator/bets/${user.id}`, { params: { date: historyDate, limit: 200 } }),
          API.get(`/wallet/${user.id}`),
        ]);
        if (!alive) return;
        setGameState(stateRes.data || null);
        setBetHistory(Array.isArray(betsRes.data?.bets) ? betsRes.data.bets : []);
        setWallet(Number(walletRes.data?.balance || 0));
      } catch (err) {
        if (!alive) return;
        setMessage(err?.response?.data?.message || "Unable to load Aviator");
      } finally {
        const remainingMs = Math.max(0, initialLoaderMs - (Date.now() - openedAt));
        loadingTimer = setTimeout(() => {
          if (alive) setLoading(false);
        }, remainingMs);
      }
    })();
    return () => {
      alive = false;
      if (loadingTimer) clearTimeout(loadingTimer);
    };
  }, [historyDate, user]);

  useEffect(() => {
    if (!user?.id) return undefined;
    const socketBase = String(API_BASE_URL || "").replace(/\/api$/i, "");
    const socketPath = /\/api$/i.test(String(API_BASE_URL || "")) && !/localhost|127\.0\.0\.1/i.test(socketBase)
      ? "/api/socket.io"
      : "/socket.io";
    const socket = io(socketBase, { path: socketPath, transports: ["websocket", "polling"] });
    socketRef.current = socket;
    socket.on("aviator:state", (payload) => {
      setGameState((current) => {
        if (!payload) return null;
        const sameRound = String(current?.round_id || "") === String(payload.round_id || "");
        const preservedBets = sameRound && Array.isArray(current?.my_bets) ? current.my_bets : [];
        const preservedBalance = current?.balance ?? payload.balance;

        if (sameRound && current?.phase === "flying" && payload.phase === "flying") {
          return {
            ...current,
            ...payload,
            my_bets: preservedBets,
            balance: preservedBalance,
            server_time: current.server_time,
            started_at: current.started_at,
            crash_at: current.crash_at,
          };
        }

        return {
          ...payload,
          my_bets: preservedBets,
          balance: preservedBalance,
        };
      });
    });
    return () => socket.disconnect();
  }, [user]);

  useEffect(() => {
    if (phase === "flying") return undefined;
    flightTimingRef.current = null;
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }
    setDisplayMultiplier(stateMultiplier);
    setFlightClock(0);
    return undefined;
  }, [phase, roundId, stateMultiplier]);

  useEffect(() => {
    if (!roundId || phase !== "flying") return undefined;
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    }

    const crashMultiplier = stateCrashMultiplier || 1;
    const serverTime = stateServerTime || Date.now();
    const startedAt = stateStartedAt || serverTime;
    const serverElapsed = Math.max(0, serverTime - startedAt);
    const durationMs = stateCrashAt && stateStartedAt
      ? Math.max(0, stateCrashAt - stateStartedAt)
      : durationForMultiplier(crashMultiplier);

    flightTimingRef.current = {
      clientStartedAt: Date.now() - serverElapsed,
      durationMs,
      crashMultiplier,
    };

    let lastRenderAt = 0;
    const renderInterval = isIOSWebView ? 34 : 16;

    const tick = () => {
      const timing = flightTimingRef.current;
      if (!timing) return;
      const elapsed = Math.max(0, Date.now() - timing.clientStartedAt);
      const projected = Math.min(timing.crashMultiplier, liveMultiplierAt(elapsed));
      const shouldRender = elapsed - lastRenderAt >= renderInterval || projected >= timing.crashMultiplier;
      if (shouldRender) {
        lastRenderAt = elapsed;
        setDisplayMultiplier(projected);
        setFlightClock(elapsed);
      }
      if (elapsed < timing.durationMs && projected < timing.crashMultiplier) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    tick();
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      }
    };
  }, [isIOSWebView, phase, roundId, stateServerTime, stateStartedAt, stateCrashAt, stateCrashMultiplier]);

  useEffect(() => {
    if (soundOn && appVisible) return undefined;
    if (flightAudioStartTimerRef.current) {
      clearTimeout(flightAudioStartTimerRef.current);
      flightAudioStartTimerRef.current = null;
    }
    stopFlightAudio();
    stopCrashAudio();
    if (startChimeRef.current) {
      if (startChimeStopRef.current) clearTimeout(startChimeStopRef.current);
      startChimeRef.current.pause();
      startChimeRef.current.currentTime = 0;
    }
    return undefined;
  }, [appVisible, soundOn, stopCrashAudio, stopFlightAudio]);

  useEffect(() => {
    const syncVisibility = () => {
      const visible = !document.hidden && (isIOSWebView || document.hasFocus());
      setAppVisible(visible);
    };
    const hideAudio = () => setAppVisible(false);

    syncVisibility();
    document.addEventListener("visibilitychange", syncVisibility);
    window.addEventListener("focus", syncVisibility);
    window.addEventListener("blur", hideAudio);
    window.addEventListener("pagehide", hideAudio);

    return () => {
      document.removeEventListener("visibilitychange", syncVisibility);
      window.removeEventListener("focus", syncVisibility);
      window.removeEventListener("blur", hideAudio);
      window.removeEventListener("pagehide", hideAudio);
    };
  }, [isIOSWebView]);

  useEffect(() => {
    if (phase !== "betting") {
      setBetPanelLocked(false);
      return undefined;
    }
    setBetPanelLocked(true);
    const timer = setTimeout(() => setBetPanelLocked(false), betPanelLockMs);
    return () => clearTimeout(timer);
  }, [phase, roundId]);

  useEffect(() => {
    if (phase !== "betting" || !roundId || !soundOn || !appVisible) return undefined;
    const msUntilFlight = Math.max(0, Number(gameState?.countdown_ms || 0));
    if (!msUntilFlight) return undefined;
    const timer = setTimeout(() => {
      if (startChimeRoundRef.current === String(roundId)) return;
      startChimeRoundRef.current = String(roundId);
      playStartChime();
    }, Math.max(0, msUntilFlight - 1000));
    return () => clearTimeout(timer);
  }, [appVisible, gameState?.countdown_ms, phase, playStartChime, roundId, soundOn]);

  useEffect(() => {
    if (!gameState?.phase) return;
    if (phaseRef.current === gameState.phase) return;
    phaseRef.current = gameState.phase;

    if (flightAudioStartTimerRef.current) {
      clearTimeout(flightAudioStartTimerRef.current);
      flightAudioStartTimerRef.current = null;
    }

    if (gameState.phase === "flying") {
      const resumeDelay = Math.max(0, startChimeUntilRef.current - Date.now());
      if (resumeDelay > 25) {
        flightAudioStartTimerRef.current = setTimeout(() => {
          playFlightAudio();
          flightAudioStartTimerRef.current = null;
        }, resumeDelay);
      } else {
        playFlightAudio();
      }
    }
    if (gameState.phase === "crashed") {
      playCrashAudio();
      refreshWalletAndHistory();
      autoBetLocksRef.current = {};
    }
    if (gameState.phase === "waiting") {
      autoBetLocksRef.current = {};
    }
  }, [gameState, playCrashAudio, playFlightAudio, refreshWalletAndHistory]);

  useEffect(() => () => {
    stopAmountHold();
    if (flightAudioStartTimerRef.current) clearTimeout(flightAudioStartTimerRef.current);
    stopFlightAudio();
    stopCrashAudio();
    if (cashoutAudioRef.current) cashoutAudioRef.current.pause();
    if (startChimeStopRef.current) clearTimeout(startChimeStopRef.current);
    if (startChimeRef.current) startChimeRef.current.pause();
  }, [stopCrashAudio, stopFlightAudio]);

  useEffect(() => {
    if (loading && soundOn && appVisible) playFlightAudio();
  }, [appVisible, loading, playFlightAudio, soundOn]);

  const placeBet = useCallback(async (slotIndex, silent = false) => {
    const slot = slots[slotIndex];
    const amount = Number(slot.amount || 0);
    if (!user?.id || !amount) return;
    if (phase !== "betting") {
      updateSlot(slotIndex, { queued: true });
      setMessage("Bet queued for next round. You can cancel before takeoff.");
      return;
    }
    if (amount < minAviatorBet) {
      setMessage(`Minimum bet is ${formatCurrency(minAviatorBet)}`);
      return;
    }
    if (amount > maxAviatorBet) {
      setMessage(`Maximum bet is ${formatCurrency(maxAviatorBet)}`);
      return;
    }
    setBusySlots((current) => ({ ...current, [slotIndex]: "bet" }));
    setMessage("");
    try {
      const res = await API.post("/aviator/bet", { user_id: user.id, amount });
      if (!silent) playTone("place");
      setWallet(Number(res.data?.balance || wallet));
      await refreshWalletAndHistory();
      const placedBet = res.data?.bet ? { ...res.data.bet, client_slot_index: slotIndex } : null;
      setGameState((current) => {
        if (!current) return current;
        const mapped = mapBetsToSlots(current.my_bets);
        mapped[slotIndex] = placedBet;
        return {
          ...current,
          my_bets: mapped.filter(Boolean).slice(0, 2),
        };
      });
      updateSlot(slotIndex, { queued: false });
    } catch (err) {
      setMessage(err?.response?.data?.message || "Unable to place bet");
    } finally {
      setBusySlots((current) => ({ ...current, [slotIndex]: null }));
    }
  }, [phase, playTone, refreshWalletAndHistory, slots, user, wallet]);

  const cancelBet = useCallback(async (slotIndex) => {
    const slotBet = currentBets[slotIndex];
    if (!user?.id || !slotBet?.id || !isPendingAviatorBet(slotBet)) return;
    setBusySlots((current) => ({ ...current, [slotIndex]: "cancel" }));
    setMessage("");
    try {
      const res = await API.post("/aviator/cancel", { user_id: user.id, bet_id: slotBet.id });
      setWallet(Number(res.data?.balance || wallet));
      await refreshWalletAndHistory();
      setGameState((current) => current ? {
        ...current,
        my_bets: (current.my_bets || []).filter((bet) => String(bet.id) !== String(slotBet.id)),
      } : current);
      setMessage("Bet cancelled and amount refunded.");
    } catch (err) {
      setMessage(err?.response?.data?.message || "Unable to cancel bet");
    } finally {
      setBusySlots((current) => ({ ...current, [slotIndex]: null }));
    }
  }, [currentBets, refreshWalletAndHistory, user, wallet]);

  useEffect(() => {
    if (phase !== "betting") return;
    slots.forEach((slot, slotIndex) => {
      const slotBet = currentBets[slotIndex];
      const hasActiveBet = isPendingAviatorBet(slotBet);
      const autoKey = `${gameState?.round_id || ""}:${slotIndex}`;
      if ((slot.queued || (slot.mode === "auto" && slot.autoEnabled)) && !hasActiveBet && !autoBetLocksRef.current[autoKey]) {
        autoBetLocksRef.current[autoKey] = true;
        placeBet(slotIndex, true);
      }
    });
  }, [currentBets, gameState?.round_id, phase, placeBet, slots]);

  const cashout = useCallback(async (slotIndex, targetMultiplier = null) => {
    const slotBet = currentBets[slotIndex];
    if (!slotBet?.id || !isPendingAviatorBet(slotBet)) return;
    setBusySlots((current) => ({ ...current, [slotIndex]: "cashout" }));
    setMessage("");
    try {
      const res = await API.post("/aviator/cashout", { user_id: user.id, bet_id: slotBet.id });
      playCashoutAudio();
      const shownMultiplier = targetMultiplier ? Number(targetMultiplier) : Number(res.data?.multiplier || displayMultiplier || 1);
      const shownPayout = targetMultiplier
        ? Math.min(maxAviatorWin, Number(slotBet.amount || 0) * Number(targetMultiplier))
        : Number(res.data?.payout || 0);
      setCashoutToast({
        multiplier: shownMultiplier,
        payout: shownPayout,
      });
      setWallet(Number(res.data?.balance || wallet));
      await refreshWalletAndHistory();
      setGameState((current) => current ? {
        ...current,
        my_bets: current.my_bets?.map((bet) => String(bet.id) === String(slotBet.id) ? {
          ...bet,
          status: "won",
          payout: shownPayout,
          cashout_multiplier: shownMultiplier,
        } : bet) || [],
      } : current);
    } catch (err) {
      setMessage(err?.response?.data?.message || "Cashout failed");
    } finally {
      setBusySlots((current) => ({ ...current, [slotIndex]: null }));
    }
  }, [currentBets, displayMultiplier, playCashoutAudio, refreshWalletAndHistory, user, wallet]);

  useEffect(() => {
    if (!cashoutToast) return undefined;
    const timer = setTimeout(() => setCashoutToast(null), 6200);
    return () => clearTimeout(timer);
  }, [cashoutToast]);

  useEffect(() => {
    if (phase !== "flying") return;
    slots.forEach((slot, slotIndex) => {
      const slotBet = currentBets[slotIndex];
      const autoValue = Number(sanitizeAutoCashout(slot.autoCashout));
      if (
        slot.mode === "auto" &&
        slotBet &&
        String(slotBet.status || "").toLowerCase() === "pending" &&
        slot.autoCashoutEnabled !== false &&
        autoValue > 1 &&
        displayMultiplier >= autoValue &&
        !busySlots[slotIndex]
      ) {
        cashout(slotIndex, autoValue);
      }
    });
  }, [busySlots, cashout, currentBets, displayMultiplier, phase, slots]);

  if (loading) {
    return (
      <div style={loaderPage} className={isIOSWebView ? "aviator-loader aviator-ios" : "aviator-loader"}>
        <style>{styles}</style>
        <div style={loaderCard}>
          <img src="/mk_aviator_official_partners_transparent.png" alt="Matka Kings Aviator official partners" style={loaderPartnerLogo} />
          <img src={aviatorAssetLogo} alt="Aviator" style={loaderLogo} />
          <div style={loaderText}>Loading live round...</div>
        </div>
        <div style={loaderPoweredBy}>
          <span>POWERED BY</span>
          <img src="/mk-logo192.png" alt="MK Casino" style={loaderPoweredByLogo} />
          <strong>MK CASINO</strong>
        </div>
      </div>
    );
  }

  const showRoundLoader = phase === "betting";
  const countdownMs = Math.max(0, Number(gameState?.countdown_ms || 0) - Math.max(0, waitClock - Number(gameState?.server_time || waitClock)));
  const loadingProgress = phase === "betting" ? clamp(countdownMs / roundWaitMs, 0, 1) : 0;
  const flightProgress = getFlightProgress(phase === "crashed" ? stateCrashMultiplier : displayMultiplier, phase);
  const trailProgress = phase === "flying" || phase === "crashed" ? clamp(flightProgress, 0.02, 1) : 0;
  const tailBobY = phase === "flying" ? Math.sin(flightClock * 0.004) * 6 : 0;
  const highMotion = getHighMultiplierMotion(displayMultiplier, phase, flightClock);
  const curvePath = buildCurvePath(trailProgress, tailBobY + highMotion.y);
  const fillPath = buildFillPath(trailProgress, tailBobY + highMotion.y);
  const point = getPointOnCurve(flightProgress);
  const planeX = point.x + planeTailOffset.x + highMotion.x;
  const planeY = point.y + planeTailOffset.y + tailBobY + highMotion.y;
  const stageTone = getStageTone(displayMultiplier, phase);

  return (
    <div style={page} className={isIOSWebView ? "aviator-page aviator-ios" : "aviator-page"}>
      <style>{styles}</style>
      <header style={topbar} className="aviator-topbar">
        <div style={brandRow}>
          <button type="button" style={iconButton} onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={15} />
          </button>
          <img src={aviatorAssetLogo} alt="Aviator" style={aviatorLogo} />
        </div>
        <div style={walletRow}>
          <span style={walletAmount}>{formatCurrency(wallet)}</span>
          <button type="button" style={iconButton} onClick={() => setSoundOn((current) => !current)}>
            {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          <button type="button" style={iconButton} onClick={() => setShowTopMenu((current) => !current)}>
            <Menu size={15} />
          </button>
        </div>
      </header>
      {showTopMenu ? (
        <div style={topMenuPanel}>
          <button type="button" style={topMenuItem} onClick={() => { refreshWalletAndHistory(); setInfoPanel("history"); setShowTopMenu(false); }}>Player Bet History</button>
          <button type="button" style={topMenuItem} onClick={() => setInfoPanel("how")}>How to Play Aviator</button>
          <button type="button" style={topMenuItem} onClick={() => setSoundOn((current) => !current)}>Sound {soundOn ? "Off" : "On"}</button>
          <button type="button" style={topMenuItem} onClick={() => setInfoPanel("rules")}>Game Rules</button>
        </div>
      ) : null}
      {cashoutToast ? (
        <div style={cashoutToastBox}>
          <div style={cashoutToastLeft}>
            <span>You have cashed out!</span>
            <b>{cashoutToast.multiplier.toFixed(2)}x</b>
          </div>
          <div style={cashoutToastWin}>
            <span>Win INR</span>
            <b>{Number(cashoutToast.payout || 0).toFixed(2)}</b>
          </div>
          <button type="button" style={cashoutToastClose} onClick={() => setCashoutToast(null)}>×</button>
        </div>
      ) : null}

      <div style={historyStrip} className="aviator-history-strip">
        {historyItems.slice(0, 10).map((item) => (
          <span key={item.round_id} style={historyBadge(Number(item.multiplier || 1))}>
            {Number(item.multiplier || 1).toFixed(2)}x
          </span>
        ))}
        <button type="button" style={historyMoreButton} onClick={() => setShowHistoryPanel((current) => !current)}>
          ...
        </button>
      </div>
      {showHistoryPanel ? (
        <div style={historyPanel} className="aviator-history-panel">
          {historyItems.slice(0, 40).map((item) => (
            <span key={`full-${item.round_id}`} style={historyBadge(Number(item.multiplier || 1))}>
              {Number(item.multiplier || 1).toFixed(2)}x
            </span>
          ))}
        </div>
      ) : null}

      <section style={arenaCard(stageTone)} className="aviator-stage-card">
        <div style={arena}>
          <div style={rayBackdrop} className={`rayBackdrop moving ${displayMultiplier >= 2 ? "purple" : ""}`} />
          <div style={arenaGlow} />
          <svg viewBox="0 0 1000 520" preserveAspectRatio="none" style={curveSvg}>
            <defs>
              <linearGradient id="flightFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(255,0,56,0.30)" />
                <stop offset="100%" stopColor="rgba(90,0,12,0.80)" />
              </linearGradient>
            </defs>
            {trailProgress > 0.015 ? <path d={fillPath} fill="url(#flightFill)" /> : null}
            {trailProgress > 0.01 ? <path d={curvePath} fill="none" stroke="#ff0b35" strokeWidth="7" strokeLinecap="round" style={curveStrokeStyle} /> : null}
          </svg>

          {showRoundLoader ? (
            <div style={roundLoaderLayer}>
              <img src="/matka_kings_aviator_no_underline_transparent.webp" alt="Matka Kings Aviator loading" style={roundLoaderImage} />
              <div style={roundLoaderTrack}>
                <div className="roundLoaderFill" style={{ ...roundLoaderFill, transform: `scaleX(${loadingProgress})` }} />
              </div>
            </div>
          ) : null}

          {!showRoundLoader ? (
          <div style={centerPanel}>
            <div style={multiplierText(phase)}>{Number(displayMultiplier || 1).toFixed(2)}x</div>
          </div>
          ) : null}

          {!showRoundLoader ? (
          <div
            style={{
              ...planeWrap,
              left: `${planeX / 10}%`,
              top: `${planeY / 5.2}%`,
              transform: "translate(-50%, -50%) rotate(0deg)",
              opacity: phase === "crashed" ? 0.82 : 1,
            }}
            className={phase === "crashed" ? "planeCrash" : ""}
          >
            <div
              aria-label="Flight"
              role="img"
              className={phase === "flying" ? "planeSprite animate" : "planeSprite"}
            />
          </div>
          ) : null}

          {phase === "crashed" ? <div style={crashText}>FLEW AWAY!</div> : null}
          <div style={playerStats}>
            <span>{totalPlayers} bets</span>
            <b>{activePlayers} active</b>
          </div>
        </div>
      </section>

      <div style={betsWrap} className="aviator-bets-wrap">
        {slots.map((slot, slotIndex) => {
      const rawSlotBet = currentBets[slotIndex] || null;
          const slotBet = isPendingAviatorBet(rawSlotBet) ? rawSlotBet : null;
          const panelLocked = betPanelLocked && phase === "betting";
          const canBet = phase === "betting" && !panelLocked && !slotBet;
          const canQueue = phase !== "betting" && !slotBet && !slot.queued;
          const canCancelQueue = !slotBet && Boolean(slot.queued);
          const canCancel = phase === "betting" && !panelLocked && Boolean(slotBet);
          const canCashout = phase === "flying" && Boolean(slotBet);
          const cashoutValue = Math.min(maxAviatorWin, Number(slotBet?.amount || 0) * Number(displayMultiplier || 1));
          const mode = slot.mode;
          const buttonText = canCashout
            ? `Cashout\n${formatCurrency(cashoutValue)}`
            : canCancelQueue
              ? `Cancel ${formatCurrency(Number(slot.amount || 0))}`
              : canCancel
              ? `Cancel ${formatCurrency(Number(slotBet.amount || 0))}`
            : formatCurrency(Number(slot.amount || 0));
          const actionDisabled = Boolean(busySlots[slotIndex]) || (!canBet && !canQueue && !canCancelQueue && !canCashout && !canCancel);
          const actionBusyText = busySlots[slotIndex] === "cashout"
            ? "Cashing..."
            : busySlots[slotIndex] === "cancel"
              ? "Cancelling..."
              : busySlots[slotIndex] === "bet"
                ? "Placing..."
                : buttonText;

          return (
            <section key={slotIndex} style={panelLocked ? { ...betCard, ...betCardDisabled } : betCard}>
              <div style={modeTabs}>
                <button type="button" style={modeTab(mode === "bet")} onClick={() => updateSlot(slotIndex, { mode: "bet" })}>Bet</button>
                <button type="button" style={modeTab(mode === "auto")} onClick={() => updateSlot(slotIndex, { mode: "auto", autoEnabled: true, autoCashoutEnabled: slot.autoCashoutEnabled ?? false })}>Auto</button>
              </div>
              <div style={betLimitNote} className="aviator-bet-limit">Min {formatCurrency(minAviatorBet)} - Max bet {formatCurrency(maxAviatorBet)} - Max win {formatCurrency(maxAviatorWin)}</div>

              {mode === "auto" ? (
                <div style={autoLayout} className="aviator-bet-grid">
                  <div style={amountBlock}>
                    <div style={amountRow}>
                      <button type="button" style={roundButton} onPointerDown={() => startAmountHold(slotIndex, -1)} onPointerUp={stopAmountHold} onPointerLeave={stopAmountHold} onPointerCancel={stopAmountHold}>-</button>
                      <input
                        value={slot.amount}
                        inputMode="numeric"
                        style={amountInput}
                        onChange={(event) => updateSlot(slotIndex, { amount: event.target.value.replace(/[^\d]/g, ""), lastQuickAmount: null })}
                        onBlur={() => normalizeSlotAmount(slotIndex)}
                      />
                      <button type="button" style={roundButton} onPointerDown={() => startAmountHold(slotIndex, 1)} onPointerUp={stopAmountHold} onPointerLeave={stopAmountHold} onPointerCancel={stopAmountHold}>+</button>
                    </div>
                    <div style={quickLine}>
                      {quickAmounts.map((value) => (
                        <button key={value} type="button" style={quickLineButton(Number(slot.lastQuickAmount) === value)} onClick={() => addQuickAmount(slotIndex, value)}>
                          {formatCompactAmount(value)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    style={actionButton(canCashout ? "#ff8a00" : (canCancel || canCancelQueue) ? "#ff0038" : "#2db500")}
                    disabled={actionDisabled}
                    onClick={() => (canCashout ? cashout(slotIndex) : canCancel ? cancelBet(slotIndex) : canCancelQueue ? updateSlot(slotIndex, { queued: false }) : placeBet(slotIndex))}
                  >
                    {actionBusyText}
                  </button>
                  <div style={autoOptionsStrip}>
                    <label style={autoSwitchLabel}>
                      <span>Auto bet</span>
                      <input
                        type="checkbox"
                        style={switchInput}
                        checked={Boolean(slot.autoEnabled)}
                        onChange={(event) => updateSlot(slotIndex, { autoEnabled: event.target.checked, mode: "auto" })}
                      />
                    </label>
                    <label style={autoSwitchLabel}>
                      <span>Auto Cash Out</span>
                      <input
                        type="checkbox"
                        style={switchInput}
                        checked={Boolean(slot.autoCashoutEnabled)}
                        onChange={(event) => updateSlot(slotIndex, { autoCashoutEnabled: event.target.checked, mode: "auto" })}
                      />
                    </label>
                    <div style={autoCashoutPill}>
                      <input
                        value={slot.autoCashout}
                        onChange={(event) => updateSlot(slotIndex, { autoCashout: event.target.value.replace(/[^\d.]/g, "") })}
                        onBlur={() => updateSlot(slotIndex, { autoCashout: sanitizeAutoCashout(slot.autoCashout) })}
                        style={autoInput}
                      />
                      <button type="button" style={autoClearButton} onClick={() => updateSlot(slotIndex, { autoCashout: "1.10", autoCashoutEnabled: false })}>×</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={betGrid} className="aviator-bet-grid">
                  <div>
                    <div style={amountRow}>
                      <button type="button" style={roundButton} onPointerDown={() => startAmountHold(slotIndex, -1)} onPointerUp={stopAmountHold} onPointerLeave={stopAmountHold} onPointerCancel={stopAmountHold}>-</button>
                      <input
                        value={slot.amount}
                        inputMode="numeric"
                        style={amountInput}
                        onChange={(event) => updateSlot(slotIndex, { amount: event.target.value.replace(/[^\d]/g, ""), lastQuickAmount: null })}
                        onBlur={() => normalizeSlotAmount(slotIndex)}
                      />
                      <button type="button" style={roundButton} onPointerDown={() => startAmountHold(slotIndex, 1)} onPointerUp={stopAmountHold} onPointerLeave={stopAmountHold} onPointerCancel={stopAmountHold}>+</button>
                    </div>
                    <div style={quickLine}>
                      {quickAmounts.map((value) => (
                        <button key={value} type="button" style={quickLineButton(Number(slot.lastQuickAmount) === value)} onClick={() => addQuickAmount(slotIndex, value)}>
                          {formatCompactAmount(value)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="button"
                    style={actionButton(canCashout ? "#ff8a00" : (canCancel || canCancelQueue) ? "#ff0038" : "#2db500")}
                    disabled={actionDisabled}
                    onClick={() => (canCashout ? cashout(slotIndex) : canCancel ? cancelBet(slotIndex) : canCancelQueue ? updateSlot(slotIndex, { queued: false }) : placeBet(slotIndex))}
                  >
                    {actionBusyText}
                  </button>
                </div>
              )}

              {slotBet ? (
                <button
                  type="button"
                  style={liveBetTag(canCashout, canCancel)}
                  disabled={!canCashout && !canCancel}
                  onClick={() => (canCashout ? cashout(slotIndex) : canCancel ? cancelBet(slotIndex) : null)}
                >
                  <span>{canCashout ? "Cashout now" : canCancel ? "Tap to cancel" : formatLiveBetStatus(slotBet.status)}</span>
                  <b>{canCashout ? formatCurrency(cashoutValue) : formatCurrency(Number(slotBet.amount || 0))}</b>
                </button>
              ) : rawSlotBet && String(rawSlotBet.status || "").toLowerCase() === "won" ? (
                <button
                  type="button"
                  style={liveBetTag(false, false)}
                  disabled
                >
                  <span>Cashed out</span>
                  <b>{formatCurrency(Number(rawSlotBet.payout || 0))}</b>
                </button>
              ) : slot.queued ? (
                <button
                  type="button"
                  style={liveBetTag(false, true)}
                  onClick={() => updateSlot(slotIndex, { queued: false })}
                >
                  <span>Queued for next round</span>
                  <b>{formatCurrency(Number(slot.amount || 0))}</b>
                </button>
              ) : null}
            </section>
          );
        })}
      </div>

      <section style={recentBetsCard} className="aviator-recent-card">
        <div style={betTabs}>
          <button type="button" style={betTabButton(betTab === "all")} onClick={() => setBetTab("all")}>All Bets</button>
          <button type="button" style={betTabButton(betTab === "previous")} onClick={() => setBetTab("previous")}>Previous</button>
          <button type="button" style={betTabButton(betTab === "top")} onClick={() => setBetTab("top")}>Top</button>
        </div>
        {betTab === "previous" ? (
          <div style={roundResultBox}>
            <span>Round Result</span>
            <strong style={roundResultValue}>{previousRoundMultiplier.toFixed(2)}x</strong>
          </div>
        ) : null}
        {betTab === "all" ? (
          <div style={allBetsSummary}>
            <div style={summaryAvatars}>
              <span style={recentBetAvatar(0)}>{playerInitials(recentFeed[0]?.player_name)}</span>
              <span style={{ ...recentBetAvatar(1), marginLeft: -12 }}>{playerInitials(recentFeed[1]?.player_name)}</span>
            </div>
            <div style={summaryCount}><b>{activePlayers}/{totalPlayers}</b> Bets</div>
            <div style={summaryWin}><b>{formatCurrency(roundTotalWin)}</b><span>Total win INR</span></div>
            <div style={summaryBar}><span style={{ ...summaryBarFill, width: `${clamp((activePlayers / Math.max(totalPlayers, 1)) * 100, 4, 100)}%` }} /></div>
          </div>
        ) : null}
        {betTab === "top" ? (
          <>
            <div style={topFilterGrid}>
              <button type="button" style={topFilterButton(topMetric === "x")} onClick={() => setTopMetric("x")}>X</button>
              <button type="button" style={topFilterButton(topMetric === "win")} onClick={() => setTopMetric("win")}>Win</button>
              <button type="button" style={topFilterButton(topMetric === "rounds")} onClick={() => setTopMetric("rounds")}>Rounds</button>
              <button type="button" style={topFilterButton(topPeriod === "day")} onClick={() => setTopPeriod("day")}>Day</button>
              <button type="button" style={topFilterButton(topPeriod === "month")} onClick={() => setTopPeriod("month")}>Month</button>
              <button type="button" style={topFilterButton(topPeriod === "year")} onClick={() => setTopPeriod("year")}>Year</button>
            </div>
            <div style={topHistoryList}>
              {topHistoryRows.map((entry, index) => (
                <div key={`${entry.player}-${index}`} style={topHistoryCard}>
                  <span style={recentBetAvatar(index)}>{playerInitials(entry.player)}</span>
                  <div style={topHistoryMain}>
                    <strong>{entry.player}</strong>
                    <small>{entry.date}</small>
                    <span>Bet INR <b>{formatCurrency(entry.bet)}</b></span>
                    <span>Win INR <b>{formatCurrency(entry.win)}</b></span>
                  </div>
                  <div style={topHistoryResult}>
                    <span>Result</span>
                    <b>{formatCompactAmount(entry.result)}.00x</b>
                    <span>Round max.</span>
                    <b>{formatCompactAmount(entry.roundMax)}.35x</b>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
        <div style={recentBetsTable} className="aviator-recent-table">
          <div style={recentBetsTableHead}>
            <span>Player</span>
            <span>Bet INR</span>
            <span>X</span>
            <span>Win INR</span>
          </div>
          {displayedFeed.length ? displayedFeed.map((entry, index) => (
            <div key={`${entry.id || entry.player_name || "feed"}-${index}`} style={recentBetRow(entry.status)}>
              <span style={recentBetPlayerWrap}>
                <span style={recentBetAvatar(index)}>{playerInitials(entry.player_name)}</span>
                <span style={recentBetPlayer}>{entry.player_name || "Demo Player"}</span>
              </span>
              <span>{formatCurrency(Number(entry.amount || 0))}</span>
              <span style={recentBetStatus(entry.status)}>
                {formatFeedMultiplier(entry, displayMultiplier)}
              </span>
              <span style={recentBetWin(entry.status)}>{formatCurrency(Number(entry.payout || 0))}</span>
            </div>
          )) : (
            <div style={emptyFeed}>Waiting for bets...</div>
          )}
        </div>
        )}
      </section>

      {message ? <div style={messageBox}>{message}</div> : null}
      {infoPanel ? (
        <div style={infoOverlay} onClick={() => setInfoPanel(null)}>
          <div style={infoCard} onClick={(event) => event.stopPropagation()}>
            <button type="button" style={infoCloseButton} onClick={() => setInfoPanel(null)}>×</button>
            <h3 style={infoTitle}>{infoPanel === "history" ? "Player Bet History" : infoPanel === "how" ? "How to Play Aviator" : "Game Rules"}</h3>
            {infoPanel === "how" ? (
              <div style={howToContent}>
                <h4>Aviator is easy to play as 1-2-3</h4>
                <div style={howSteps}>
                  <div><b>01</b><strong>BET</strong><span>Choose an amount and place your bet before take-off.</span></div>
                  <div><b>02</b><strong>WATCH</strong><span>The multiplier starts at 1x and grows while the Lucky Plane flies.</span></div>
                  <div><b>03</b><strong>CASH OUT</strong><span>Cash out before the plane flies away to win at that multiplier.</span></div>
                </div>
                <h4>More details</h4>
                <ul style={howList}>
                  <li>The win multiplier starts at 1x and grows more and more as the Lucky Plane takes off.</li>
                  <li>Your winnings are calculated at the multiplier where you cash out, multiplied by your bet.</li>
                  <li>Before each round, generated multipliers are created by a provably fair random-number system.</li>
                  <li>Select an amount and press the Bet button to make a bet.</li>
                  <li>You can cancel the bet before the round starts.</li>
                  <li>You can make two bets at the same time using both bet panels.</li>
                  <li>Press Cashout to collect winnings before the plane disappears.</li>
                  <li>Auto Bet and Auto Cash Out can place and cash out automatically based on your settings.</li>
                  <li>Live Bets shows current round bets; Top shows biggest dummy statistics.</li>
                  <li>Use the top menu for sound, player bet history, rules, and how to play.</li>
                </ul>
              </div>
            ) : infoPanel === "history" ? (
              <div style={historyList}>
                <input type="date" value={historyDate} onChange={(event) => setHistoryDate(event.target.value)} style={aviatorHistoryDateInput} />
                {betHistory.length ? betHistory.map((bet) => (
                  <div key={bet.id} style={historyBetRow}>
                    <div>
                      <strong>{formatCurrency(Number(bet.amount || 0))}</strong>
                      <span>{String(bet.round_id || "").slice(0, 18)}</span>
                    </div>
                    <div style={historyBetRight}>
                      <b style={historyBetStatus(bet.status)}>{formatBetHistoryStatus(bet)}</b>
                      <span>{formatCurrency(Number(bet.payout || 0))}</span>
                    </div>
                  </div>
                )) : (
                  <div style={emptyFeed}>No Aviator bets yet.</div>
                )}
              </div>
            ) : (
              <div style={infoText}>
                Minimum bet is {formatCurrency(minAviatorBet)}. Maximum bet is {formatCurrency(maxAviatorBet)}. Maximum win per bet is {formatCurrency(maxAviatorWin)}. Cancel is available only before takeoff.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );

  function updateSlot(index, updates) {
    setSlots((current) => current.map((slot, slotIndex) => (slotIndex === index ? { ...slot, ...updates } : slot)));
  }

  function addQuickAmount(index, amount) {
    setSlots((current) => current.map((slot, slotIndex) => {
      if (slotIndex !== index) return slot;
      const currentAmount = Number(slot.amount || 0);
      const quickAmount = Number(amount || 0);
      const sameQuickAmount = Number(slot.lastQuickAmount || 0) === quickAmount;
      const nextAmount = sameQuickAmount
        ? clamp(currentAmount + quickAmount, minAviatorBet, maxAviatorBet)
        : clamp(quickAmount, minAviatorBet, maxAviatorBet);
      return { ...slot, amount: String(nextAmount), lastQuickAmount: quickAmount };
    }));
  }

  function normalizeSlotAmount(index) {
    setSlots((current) => current.map((slot, slotIndex) => {
      if (slotIndex !== index) return slot;
      const numericAmount = Number(slot.amount || minAviatorBet);
      return { ...slot, amount: String(clamp(Math.floor(numericAmount), minAviatorBet, maxAviatorBet)), lastQuickAmount: null };
    }));
  }

  function stopAmountHold() {
    const hold = amountHoldRef.current;
    if (!hold) return;
    clearTimeout(hold.timeoutId);
    clearInterval(hold.intervalId);
    amountHoldRef.current = null;
  }

  function startAmountHold(index, direction) {
    stopAmountHold();
    adjustSlotAmount(index, direction);
    let speed = 1;
    const hold = {
      timeoutId: null,
      intervalId: null,
    };
    hold.timeoutId = setTimeout(() => {
      hold.intervalId = setInterval(() => {
        speed = Math.min(50, speed + 2);
        adjustSlotAmount(index, direction * speed);
      }, 85);
    }, 320);
    amountHoldRef.current = hold;
  }

  function adjustSlotAmount(index, direction) {
    setSlots((current) => current.map((slot, slotIndex) => {
      if (slotIndex !== index) return slot;
      const currentAmount = Number(slot.amount || 100);
      const nextAmount = clamp(currentAmount + direction, minAviatorBet, maxAviatorBet);
      return { ...slot, amount: String(nextAmount), lastQuickAmount: null };
    }));
  }
}

function liveMultiplierAt(elapsedMs) {
  return getMultiplier(elapsedMs);
}

function getMultiplier(elapsedMs) {
  const elapsedSeconds = Math.max(0, elapsedMs) / 1000;
  return Math.exp(growthRate * elapsedSeconds);
}

function durationForMultiplier(multiplier) {
  const safeMultiplier = Math.max(1.01, Number(multiplier || 1));
  return Math.ceil((Math.log(safeMultiplier) / growthRate) * 1000);
}

function sanitizeAutoCashout(value) {
  const clean = String(value || "").replace(/[^\d.]/g, "");
  const numeric = Number(clean);
  if (!clean) return "1.10";
  if (!Number.isFinite(numeric) || numeric < 1.1) return "1.10";
  return numeric.toFixed(2);
}

function getFlightProgress(multiplier, phase) {
  if (phase !== "flying" && phase !== "crashed") return 0;
  const safeMultiplier = Math.max(1, Number(multiplier || 1));
  const progress = (safeMultiplier - 1) / 1.08;
  return clamp(progress, 0, 0.82);
}

function getHighMultiplierMotion(multiplier, phase, clock) {
  if (phase !== "flying" && phase !== "crashed") return { x: 0, y: 0 };
  const safeMultiplier = Math.max(1, Number(multiplier || 1));
  if (safeMultiplier <= 10) return { x: 0, y: 0 };

  const intensity = clamp(Math.log(safeMultiplier / 10) / Math.log(35), 0, 1);
  const wave = Math.sin(clock * 0.0017);
  const secondWave = Math.sin(clock * 0.0009 + 1.4);

  return {
    x: secondWave * 18 * intensity,
    y: (26 + wave * 42) * intensity,
  };
}

function getStageTone(multiplier, phase) {
  if (phase === "crashed") {
    return {
      top: "#160307",
      bottom: "#050405",
      glow: "rgba(255, 20, 48, 0.32)",
      border: "rgba(255, 20, 48, 0.38)",
      inset: "rgba(255, 20, 48, 0.12)",
    };
  }

  const value = Number(multiplier || 1);
  if (value >= 10) {
    return {
      top: "#07150c",
      bottom: "#030504",
      glow: "rgba(33, 228, 135, 0.26)",
      border: "rgba(33, 228, 135, 0.24)",
      inset: "rgba(33, 228, 135, 0.08)",
    };
  }
  if (value >= 2) {
    return {
      top: "#07131f",
      bottom: "#030506",
      glow: "rgba(70, 180, 255, 0.30)",
      border: "rgba(70, 180, 255, 0.22)",
      inset: "rgba(70, 180, 255, 0.08)",
    };
  }

  return {
    top: "#08070a",
    bottom: "#040405",
    glow: "rgba(255, 20, 48, 0.16)",
    border: "rgba(255, 255, 255, 0.08)",
    inset: "rgba(255, 20, 48, 0.05)",
  };
}

function getPointOnCurve(progress) {
  const t = clamp(progress, 0, 1);
  const x = (1 - t) * (1 - t) * curveStart.x + 2 * (1 - t) * t * curveControl.x + t * t * curveEnd.x;
  const y = (1 - t) * (1 - t) * curveStart.y + 2 * (1 - t) * t * curveControl.y + t * t * curveEnd.y;
  return { x, y };
}

function buildCurvePath(progress, endYOffset = 0) {
  const point = getPointOnCurve(progress);
  const control = getPartialCurveControl(progress);
  return `M ${curveStart.x} ${curveStart.y} Q ${control.x} ${control.y} ${point.x} ${point.y + endYOffset}`;
}

function buildFillPath(progress, endYOffset = 0) {
  const point = getPointOnCurve(progress);
  return `${buildCurvePath(progress, endYOffset)} L ${point.x} 520 L ${curveStart.x} 520 Z`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getPartialCurveControl(progress) {
  const t = clamp(progress, 0, 1);
  return {
    x: curveStart.x + (curveControl.x - curveStart.x) * t,
    y: curveStart.y + (curveControl.y - curveStart.y) * t,
  };
}

function formatCurrency(value) {
  return `₹${Number(value || 0).toFixed(2)}`;
}

function formatCompactAmount(value) {
  return Number(value).toLocaleString("en-IN");
}

function buildTopHistoryRows(metric, period) {
  const now = Date.now();
  const periodMs = period === "day" ? 4 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
  const bucket = Math.floor(now / periodMs);
  const periodSeed = period === "year" ? 7900 : period === "month" ? 4300 : 1300;
  const metricSeed = metric === "win" ? 170 : metric === "rounds" ? 310 : 60;
  const names = ["h***0", "r***4", "m***8", "d***0", "k***7", "s***2", "a***9", "v***5", "p***3", "n***6", "y***1", "t***5"];

  return names.map((player, index) => {
    const seed = seededUnit(bucket + periodSeed + metricSeed, index + 1);
    const secondSeed = seededUnit(bucket + periodSeed, index + 9);
    const dateSeed = seededUnit(bucket + metricSeed, index + 33);
    const result = Math.max(2, Math.round(12 + seed * (period === "day" ? 420 : period === "month" ? 760 : 988)));
    const roundMax = Math.min(1000, Math.max(result + 8, Math.round(result + 24 + secondSeed * 180)));
    const targetWin = pickTopTargetWin(seed, secondSeed, period);
    const adjustedBet = Math.max(10, Math.round((targetWin / Math.max(result, 1)) / 10) * 10);
    const win = Math.min(maxAviatorWin, Number((adjustedBet * result).toFixed(2)));
    return {
      player,
      date: buildRandomHistoryDate(dateSeed),
      bet: adjustedBet,
      result,
      roundMax,
      win,
      rounds: 180 - index * 11,
    };
  }).sort((a, b) => metric === "win" ? b.win - a.win : metric === "rounds" ? b.rounds - a.rounds : b.result - a.result);
}

function pickTopTargetWin(seed, secondSeed, period) {
  const baseMax = period === "day" ? 140000 : period === "month" ? 280000 : 420000;
  if (seed > 0.94) return 350000 + secondSeed * 150000;
  if (seed > 0.82) return 180000 + secondSeed * Math.min(170000, baseMax);
  if (seed > 0.58) return 65000 + secondSeed * Math.min(135000, baseMax);
  return 10000 + secondSeed * Math.min(90000, baseMax);
}

function seededUnit(seed, index) {
  return Math.abs(Math.sin(seed * 12.9898 + index * 78.233) * 43758.5453) % 1;
}

function buildRandomHistoryDate(seed) {
  const start = new Date("2018-01-01T00:00:00Z").getTime();
  const end = Date.now();
  const date = new Date(start + seed * (end - start));
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getFullYear()).slice(-2)}`;
}

function formatFeedMultiplier(entry, liveMultiplier) {
  const normalized = String(entry?.status || "pending").toLowerCase();
  if (normalized === "won") {
    const x = Number(entry?.cashout_multiplier || 0);
    return `${x.toFixed(2)}x`;
  }
  if (normalized === "lost") {
    const x = Number(entry?.crash_multiplier || 0);
    return x ? `${x.toFixed(2)}x` : "-";
  }
  if (normalized === "cancelled") return "cancelled";
  const currentX = Math.max(1, Number(liveMultiplier || 1));
  return `${currentX.toFixed(2)}x`;
}

function formatBetHistoryStatus(bet) {
  const normalized = String(bet?.status || "pending").toLowerCase();
  if (normalized === "won") return `${Number(bet?.cashout_multiplier || 0).toFixed(2)}x won`;
  if (normalized === "lost") return `${Number(bet?.crash_multiplier || 0).toFixed(2)}x lost`;
  if (normalized === "cancelled") return "cancelled";
  return "bet success - result pending";
}

function formatLiveBetStatus(status) {
  const normalized = String(status || "pending").toLowerCase();
  if (normalized === "pending") return "Bet Success";
  if (normalized === "won") return "Cashed out";
  if (normalized === "lost") return "Lost";
  if (normalized === "cancelled") return "Cancelled";
  return normalized.toUpperCase();
}

function playerInitials(name) {
  const source = String(name || "Demo Player").trim();
  if (!source) return "DP";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function historyBadge(multiplier) {
  const value = Number(multiplier || 0);
  const color = value >= 50 ? "#ffd970"
    : value >= 10 ? "#ff3fb9"
    : value >= 2 ? "#b842ff"
    : "#77c5ff";
  return {
    ...historyPill,
    color,
    borderColor: color,
    boxShadow: `0 0 12px ${color}22`,
  };
}

function multiplierText(phase) {
  return {
    fontSize: "clamp(58px, 15vw, 126px)",
    fontWeight: 900,
    lineHeight: 1,
    color: "#ffffff",
    textShadow: "0 0 40px rgba(74,170,255,.28), 0 0 22px rgba(255,255,255,.12)",
    marginBottom: 12,
  };
}

function modeTab(active) {
  return {
    flex: 1,
    minHeight: 28,
    border: "none",
    borderRadius: 999,
    background: active ? "#2e3036" : "transparent",
    color: active ? "#fff" : "#8f95a3",
    fontWeight: 700,
  };
}

function actionButton(color) {
  const isCashout = color === "#ff8a00";
  return {
    minHeight: 70,
    border: "1px solid rgba(255,255,255,.10)",
    borderRadius: 16,
    background: color,
    color: isCashout ? "#151100" : "#fff",
    fontSize: 17,
    fontWeight: 800,
    padding: "10px 14px",
    whiteSpace: "pre-line",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,.12)",
  };
}

function quickLineButton(active) {
  return {
    minHeight: 29,
    borderRadius: 12,
    border: `1px solid ${active ? "rgba(247,196,75,.45)" : "rgba(255,255,255,.09)"}`,
    background: active ? "rgba(247,196,75,.16)" : "rgba(255,255,255,.05)",
    color: active ? "#ffd970" : "#e5e7eb",
    fontWeight: 800,
    fontSize: 13,
  };
}

const styles = `
  @keyframes fadePulse { 0%,100%{opacity:.7} 50%{opacity:1} }
  @keyframes rayDrift {
    0% { transform: rotate(0deg) scale(1); }
    50% { transform: rotate(180deg) scale(1.02); }
    100% { transform: rotate(360deg) scale(1); }
  }
  @keyframes cashoutToastFloat {
    0% {
      top: 38vh;
      opacity: 0;
      transform: translate(-50%, 22px) scale(.86);
      filter: blur(5px);
    }
    9% {
      top: 8px;
      opacity: 1;
      transform: translate(-50%, 0) scale(1);
      filter: blur(0);
    }
    88% {
      top: 8px;
      opacity: 1;
      transform: translate(-50%, 0) scale(1);
      filter: blur(0);
    }
    100% {
      top: 8px;
      opacity: 0;
      transform: translate(-50%, -8px) scale(.98);
      filter: blur(2px);
    }
  }
  @keyframes planeSpriteFan {
    0%, 49.99% { background-position: 0% 0%; }
    50%, 100% { background-position: 100% 0%; }
  }
  @keyframes crashFlash {
    0% { opacity: 1; transform: translate(-50%, -50%) rotate(0deg) scale(.88); }
    22% { opacity: 1; transform: translate(-35%, -55%) rotate(0deg) scale(.88); }
    100% { opacity: 0; transform: translate(130vw, -145%) rotate(0deg) scale(.72); }
  }
  .planeSprite {
    position: relative;
    width: 100%;
    aspect-ratio: 1024 / 512;
    background-image: url("/av2_fan_2pos_sheet_cutout.png");
    background-repeat: no-repeat;
    background-size: 200% 100%;
    background-position: 0 0;
    transform: scale(1.12);
    transform-origin: center center;
    filter: saturate(1.04) contrast(1.08);
  }
  .planeSprite.animate {
    animation: planeSpriteFan 180ms step-end infinite;
  }
  .planeCrash {
    animation: crashFlash .68s cubic-bezier(.12,.78,.18,1) forwards;
  }
  .rayBackdrop.moving {
    animation: rayDrift 54s linear infinite;
  }
  .rayBackdrop.purple {
    background:
      radial-gradient(circle at 0% 100%, rgba(224,129,255,.26) 0 7%, rgba(129,78,236,.17) 16%, rgba(50,17,100,.05) 34%, transparent 56%),
      repeating-conic-gradient(from 270deg at 0% 100%, rgba(224,129,255,.14) 0deg, rgba(224,129,255,.14) 9deg, rgba(8,8,15,.08) 9deg, rgba(8,8,15,.08) 18deg),
      radial-gradient(circle at 58% 38%, rgba(99,170,255,.12), transparent 44%) !important;
    opacity: .9;
  }
  .aviator-recent-table::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
  .roundLoaderFill {
    backface-visibility: hidden;
    transform: translateZ(0) scaleX(1);
    will-change: transform;
  }
  @supports (padding: max(0px)) {
    .aviator-page {
      padding-left: max(8px, env(safe-area-inset-left)) !important;
      padding-right: max(8px, env(safe-area-inset-right)) !important;
      padding-bottom: max(14px, env(safe-area-inset-bottom)) !important;
    }
  }
  @media (min-width: 721px) and (max-width: 1023px) {
    .aviator-page {
      max-width: 100vw !important;
      padding-bottom: 18px !important;
    }
    .aviator-stage-card {
      height: min(46vh, 430px) !important;
    }
    .aviator-bets-wrap {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    .aviator-bet-grid {
      grid-template-columns: minmax(0, .72fr) minmax(160px, 1fr) !important;
    }
  }
  input[type="checkbox"]:checked {
    background: radial-gradient(circle at 30px 50%, #f0f2f5 0 8px, transparent 9px), #1fb312 !important;
  }
  @media (max-width: 720px) {
    .aviator-bet-grid {
      grid-template-columns: minmax(0, .78fr) minmax(132px, 1fr) !important;
      gap: 8px !important;
    }
    .aviator-stage-card {
      height: 256px !important;
    }
    .aviator-page {
      padding: 0 8px 14px !important;
    }
    .aviator-history-panel {
      left: 8px !important;
      right: 8px !important;
      top: 104px !important;
      max-height: 150px !important;
      overflow-y: auto !important;
    }
    .aviator-bet-limit {
      display: none !important;
    }
  }
  @media (min-width: 1024px) {
    .aviator-page {
      width: 100vw !important;
      max-width: none !important;
      height: 100vh !important;
      min-height: 100vh !important;
      display: grid !important;
      grid-template-columns: 440px minmax(0, 1fr) !important;
      grid-template-rows: 46px 40px minmax(0, 1fr) 208px !important;
      gap: 0 8px !important;
      padding: 0 !important;
      overflow: hidden !important;
      background: #090a0c !important;
    }
    .aviator-topbar {
      grid-column: 1 / 3 !important;
      grid-row: 1 !important;
      height: 46px !important;
      min-height: 46px !important;
      margin: 0 !important;
      padding: 0 18px 0 0 !important;
      border-radius: 0 !important;
    }
    .aviator-history-strip {
      grid-column: 2 !important;
      grid-row: 2 !important;
      min-height: 40px !important;
      height: 40px !important;
      margin: 0 !important;
      padding: 0 12px !important;
      gap: 14px !important;
      overflow: hidden !important;
      border-radius: 0 !important;
    }
    .aviator-history-panel {
      left: 456px !important;
      right: 8px !important;
      top: 88px !important;
    }
    .aviator-stage-card {
      grid-column: 2 !important;
      grid-row: 3 !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 0 0 6px !important;
      border-radius: 20px !important;
    }
    .aviator-bets-wrap {
      grid-column: 2 !important;
      grid-row: 4 !important;
      height: 208px !important;
      display: grid !important;
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 8px !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }
    .aviator-bets-wrap > section {
      height: 208px !important;
      min-height: 0 !important;
      padding: 8px !important;
      border-radius: 20px !important;
      overflow: hidden !important;
    }
    .aviator-bet-grid {
      grid-template-columns: minmax(0, .62fr) minmax(190px, .86fr) !important;
      gap: 10px !important;
    }
    .aviator-bet-limit {
      display: none !important;
    }
    .aviator-recent-card {
      grid-column: 1 !important;
      grid-row: 2 / 5 !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 0 !important;
      padding: 8px !important;
      border-radius: 0 20px 20px 0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
      box-shadow: none !important;
    }
    .aviator-recent-table {
      min-height: 0 !important;
      overflow-y: auto !important;
      padding-right: 2px !important;
    }
    .aviator-recent-table::-webkit-scrollbar {
      width: 0;
      height: 0;
    }
  }
  .aviator-ios {
    min-height: calc(var(--aviator-vh, 1vh) * 100) !important;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
    transform: translateZ(0);
  }
  .aviator-ios.aviator-loader {
    min-height: calc(var(--aviator-vh, 1vh) * 100) !important;
    height: calc(var(--aviator-vh, 1vh) * 100) !important;
  }
  .aviator-ios .aviator-stage-card,
  .aviator-ios .aviator-bets-wrap > section,
  .aviator-ios .aviator-recent-card {
    contain: layout paint;
    transform: translateZ(0);
  }
  .aviator-ios .rayBackdrop.moving {
    animation-duration: 66s;
    opacity: .78;
  }
  .aviator-ios .planeSprite {
    filter: none !important;
    transform: scale(1.08) translateZ(0);
    backface-visibility: hidden;
  }
  .aviator-ios .roundLoaderFill {
    transition: transform 90ms linear !important;
  }
  .aviator-ios svg,
  .aviator-ios path {
    backface-visibility: hidden;
  }
  @media (min-width: 1024px) {
    .aviator-ios.aviator-page {
      height: calc(var(--aviator-vh, 1vh) * 100) !important;
      min-height: calc(var(--aviator-vh, 1vh) * 100) !important;
    }
  }
`;

const loaderPage = {
  minHeight: "calc(var(--aviator-vh, 1vh) * 100)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  background: "#ffffff",
  color: "#101114",
  padding: 24,
  overflow: "hidden",
};

const loaderCard = {
  textAlign: "center",
  animation: "fadePulse 1.3s ease-in-out infinite",
  width: "min(760px, 94vw)",
};

const loaderPartnerLogo = { width: "100%", maxHeight: 210, objectFit: "contain", marginBottom: 18, filter: "drop-shadow(0 10px 24px rgba(230, 0, 26, .12))" };
const loaderLogo = { width: 120, height: 120, objectFit: "contain", margin: "0 auto 14px", display: "block" };
const loaderText = { color: "#3f4652", fontSize: 15, fontWeight: 700 };
const loaderPoweredBy = {
  position: "absolute",
  left: 20,
  right: 20,
  bottom: 24,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  color: "#15171b",
  fontWeight: 900,
  letterSpacing: 1.4,
  fontSize: 14,
};
const loaderPoweredByLogo = { width: 34, height: 34, borderRadius: 10, objectFit: "cover", boxShadow: "0 8px 20px rgba(0,0,0,.14)" };

const page = {
  position: "relative",
  minHeight: "calc(var(--aviator-vh, 1vh) * 100)",
  background: "radial-gradient(circle at top, rgba(255,20,48,.12), transparent 35%), radial-gradient(circle at bottom right, rgba(247,196,75,.08), transparent 30%), #070707",
  color: "#fff",
  padding: "0 8px 22px",
  maxWidth: 1200,
  margin: "0 auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
};

const topbar = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 6,
  height: 56,
  margin: "0 -8px",
  padding: "0 12px",
  flexWrap: "nowrap",
  background: "linear-gradient(180deg,#1d1e21,#151619)",
  borderTop: "2px solid rgba(255,20,48,.75)",
  borderBottom: "1px solid rgba(255,255,255,.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,.35)",
};
const brandRow = { display: "flex", alignItems: "center", gap: 7, minWidth: 0 };
const iconButton = { width: 30, height: 30, flex: "0 0 30px", borderRadius: 9, border: "1px solid rgba(255,255,255,.09)", background: "#111111", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center" };
const aviatorLogo = { width: "clamp(82px, 24vw, 112px)", height: 28, objectFit: "contain", display: "block" };
const walletRow = { display: "flex", alignItems: "center", gap: 5, marginLeft: "auto", minWidth: 0 };
const walletAmount = { padding: "5px 7px", border: "1px solid rgba(247,196,75,.28)", borderRadius: 9, background: "linear-gradient(135deg, rgba(247,196,75,.12), rgba(255,20,48,.04))", color: "#ffd970", fontSize: "clamp(11px, 2.6vw, 13px)", lineHeight: 1, fontWeight: 800, whiteSpace: "nowrap" };
const historyStrip = {
  display: "flex",
  gap: 14,
  alignItems: "center",
  minHeight: 48,
  overflowX: "auto",
  margin: "0 -8px 8px",
  padding: "0 12px",
  scrollbarWidth: "none",
  background: "#101113",
  borderBottom: "1px solid rgba(255,255,255,.08)",
  boxShadow: "inset 0 -1px 0 rgba(0,0,0,.7)",
};
const historyPill = { flex: "0 0 auto", padding: "7px 10px", borderRadius: 999, fontWeight: 800, fontSize: 13, background: "#111111", border: "1px solid rgba(255,255,255,.09)" };
const historyMoreButton = { flex: "0 0 auto", minWidth: 44, minHeight: 34, border: "1px solid rgba(255,255,255,.09)", borderRadius: 999, background: "#2c2d31", color: "#fff", fontWeight: 900 };
const historyPanel = {
  position: "absolute",
  left: 14,
  right: 14,
  top: 104,
  zIndex: 40,
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
  padding: "10px",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 16,
  background: "#101113",
  boxShadow: "0 18px 45px rgba(0,0,0,.5)",
};
const topMenuPanel = {
  position: "fixed",
  top: 50,
  right: 10,
  zIndex: 90,
  width: 230,
  padding: 8,
  border: "1px solid rgba(255,255,255,.10)",
  borderRadius: 16,
  background: "linear-gradient(180deg,#1d1e22,#101113)",
  boxShadow: "0 18px 45px rgba(0,0,0,.58)",
};
const topMenuItem = {
  width: "100%",
  minHeight: 38,
  border: "none",
  borderRadius: 12,
  background: "transparent",
  color: "#f3f4f6",
  textAlign: "left",
  padding: "0 12px",
  fontWeight: 800,
};
const cashoutToastBox = {
  position: "fixed",
  top: 8,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 125,
  width: "min(560px, calc(100vw - 36px))",
  minHeight: 74,
  display: "grid",
  gridTemplateColumns: "1fr 1.12fr 34px",
  alignItems: "center",
  gap: 8,
  padding: "8px 10px 8px 18px",
  borderRadius: 999,
  border: "1px solid rgba(122,255,140,.55)",
  background: "linear-gradient(90deg, #005700, #006b12)",
  boxShadow: "0 18px 38px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.18)",
  color: "#eaffef",
  animation: "cashoutToastFloat 6.2s ease both",
};
const cashoutToastLeft = { display: "grid", gap: 2, textAlign: "center", lineHeight: 1.05 };
const cashoutToastWin = {
  display: "grid",
  gap: 2,
  textAlign: "center",
  lineHeight: 1.05,
  borderRadius: 999,
  padding: "9px 10px",
  background: "linear-gradient(90deg,#12b80b,#21d515)",
  boxShadow: "inset 0 0 20px rgba(255,255,255,.10)",
};
const cashoutToastClose = {
  width: 32,
  height: 32,
  border: "none",
  borderRadius: 999,
  background: "rgba(255,255,255,.12)",
  color: "#d7ffdc",
  fontSize: 24,
  fontWeight: 900,
  lineHeight: 1,
};
const infoOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 18,
  background: "rgba(0,0,0,.64)",
};
const infoCard = {
  position: "relative",
  width: "min(420px, 94vw)",
  border: "1px solid rgba(255,255,255,.10)",
  borderRadius: 20,
  background: "linear-gradient(180deg,#1c1d21,#0f1012)",
  padding: "22px 18px 18px",
  boxShadow: "0 24px 70px rgba(0,0,0,.62)",
};
const infoCloseButton = {
  position: "absolute",
  top: 8,
  right: 8,
  width: 30,
  height: 30,
  border: "none",
  borderRadius: 999,
  background: "#2c2d31",
  color: "#fff",
  fontSize: 20,
};
const infoTitle = { margin: "0 34px 12px 0", color: "#ffd970", fontSize: 18 };
const infoText = { color: "#d1d5db", lineHeight: 1.55, fontSize: 14 };
const howToContent = { color: "#d1d5db", lineHeight: 1.5, fontSize: 13 };
const howSteps = { display: "grid", gap: 10, margin: "10px 0 16px" };
const howList = { display: "grid", gap: 8, margin: "8px 0 0", paddingLeft: 18 };
const historyList = { display: "grid", gap: 8, maxHeight: "min(520px, 70vh)", overflowY: "auto", paddingRight: 2 };
const aviatorHistoryDateInput = { width: "100%", minHeight: 38, borderRadius: 12, border: "1px solid #36363c", background: "#111214", color: "#fff", padding: "0 12px", fontWeight: 800, boxSizing: "border-box" };
const historyBetRow = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 12,
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: 14,
  background: "#101113",
  border: "1px solid rgba(255,255,255,.06)",
  color: "#d1d5db",
  fontSize: 13,
};
const historyBetRight = { display: "grid", gap: 3, textAlign: "right" };
function historyBetStatus(status) {
  const normalized = String(status || "pending").toLowerCase();
  return {
    color: normalized === "won" ? "#22c55e" : normalized === "lost" ? "#ff7788" : normalized === "cancelled" ? "#ffd970" : "#77c5ff",
  };
}
const arenaCard = (tone) => ({
  position: "relative",
  height: "clamp(256px, 38vh, 511px)",
  overflow: "hidden",
  borderRadius: 24,
  border: `1px solid ${tone.border}`,
  background: `radial-gradient(circle at 46% 32%, ${tone.glow}, transparent 28%), linear-gradient(180deg, ${tone.top}, ${tone.bottom})`,
  boxShadow: `0 18px 55px rgba(0,0,0,.55), inset 0 0 80px ${tone.inset}`,
  marginBottom: 10,
});
const arena = { position: "relative", width: "100%", height: "100%" };
const rayBackdrop = {
  position: "absolute",
  left: "-62%",
  bottom: "-108%",
  width: "286%",
  height: "286%",
  background: "radial-gradient(circle at 0% 100%, rgba(110,208,255,.28) 0 7%, rgba(33,110,160,.17) 16%, rgba(11,30,52,.05) 34%, transparent 56%), repeating-conic-gradient(from 270deg at 0% 100%, rgba(148,221,255,.12) 0deg, rgba(148,221,255,.12) 9deg, rgba(3,7,12,.08) 9deg, rgba(3,7,12,.08) 18deg), radial-gradient(circle at 58% 38%, rgba(98,186,255,.12), transparent 44%)",
  opacity: 0.9,
  transformOrigin: "0% 100%",
  willChange: "transform",
  WebkitMaskImage: "radial-gradient(ellipse 136% 102% at 0% 100%, #000 0 74%, rgba(0,0,0,.9) 86%, transparent 100%)",
  maskImage: "radial-gradient(ellipse 136% 102% at 0% 100%, #000 0 74%, rgba(0,0,0,.9) 86%, transparent 100%)",
};
const arenaGlow = {
  position: "absolute",
  inset: "14px",
  borderRadius: 24,
  border: "0",
  boxShadow: "none",
  pointerEvents: "none",
};
const roundLoaderLayer = {
  position: "absolute",
  inset: 0,
  zIndex: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  padding: "28px 18px",
  background: "radial-gradient(circle at center, rgba(255,255,255,.08), transparent 44%), rgba(0,0,0,.22)",
};
const roundLoaderImage = {
  width: "min(760px, 90%)",
  maxHeight: "54%",
  objectFit: "contain",
  filter: "drop-shadow(0 0 22px rgba(255, 20, 48, .34))",
};
const roundLoaderTrack = {
  width: "min(640px, 70%)",
  height: 8,
  marginTop: 14,
  borderRadius: 999,
  overflow: "hidden",
  background: "rgba(68, 76, 88, .72)",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,.5)",
};
const roundLoaderFill = {
  width: "100%",
  height: "100%",
  borderRadius: 999,
  transformOrigin: "left center",
  background: "linear-gradient(90deg, #ff0038, #ff244b)",
  boxShadow: "0 0 16px rgba(255, 0, 56, .7)",
  transition: "transform 120ms linear",
};
const curveSvg = { position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 2, overflow: "visible" };
const curveStrokeStyle = { filter: "drop-shadow(0 0 12px rgba(255,11,53,.72))" };
const centerPanel = {
  position: "absolute",
  left: "50%",
  top: "38%",
  transform: "translate(-50%, -50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
  zIndex: 4,
  textAlign: "center",
  width: "100%",
  padding: "0 18px",
};
const planeWrap = {
  position: "absolute",
  width: "clamp(86px, 13vw, 168px)",
  zIndex: 5,
  transition: "opacity 200ms ease",
  filter: "drop-shadow(0 10px 18px rgba(0,0,0,.72))",
  transformOrigin: "center center",
  willChange: "left, top, transform",
  overflow: "hidden",
};
const playerStats = {
  position: "absolute",
  right: 14,
  bottom: 14,
  zIndex: 8,
  display: "flex",
  gap: 8,
  alignItems: "center",
  padding: "8px 10px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.09)",
  background: "rgba(0,0,0,.48)",
  color: "#cbd5e1",
  fontSize: 12,
  fontWeight: 800,
};
const crashText = {
  position: "absolute",
  left: "50%",
  top: "18%",
  transform: "translate(-50%, -50%)",
  zIndex: 6,
  color: "#ff1430",
  fontSize: "clamp(18px, 4.2vw, 38px)",
  fontWeight: 1000,
  letterSpacing: 2,
  textShadow: "0 0 30px rgba(255,20,48,.8)",
};
const betsWrap = {
  display: "grid",
  gap: 8,
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
};
const betCard = {
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 20,
  background: "linear-gradient(180deg,#17181b,#0d0e10)",
  padding: 6,
  boxShadow: "0 18px 55px rgba(0,0,0,.38)",
};
const betCardDisabled = { opacity: 0.48, pointerEvents: "none", filter: "grayscale(.25)" };
const modeTabs = { background: "#111214", borderRadius: 999, padding: 3, display: "flex", gap: 4, marginBottom: 6 };
const betLimitNote = { color: "#8f95a3", fontSize: 11, fontWeight: 700, margin: "-4px 4px 10px" };
const betGrid = { display: "grid", gridTemplateColumns: "minmax(0,.82fr) minmax(142px,1fr)", gap: 8, alignItems: "stretch" };
const autoLayout = { display: "grid", gridTemplateColumns: "minmax(0,.82fr) minmax(142px,1fr)", gap: 8, alignItems: "stretch" };
const amountBlock = { display: "flex", flexDirection: "column", gap: 6 };
const amountRow = { display: "grid", gridTemplateColumns: "30px 1fr 30px", gap: 5 };
const roundButton = { borderRadius: 999, border: "1px solid rgba(255,255,255,.09)", background: "#17181c", color: "#fff", fontSize: 18 };
const amountBox = { minHeight: 34, borderRadius: 13, background: "#17181c", border: "1px solid rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800 };
const amountInput = { ...amountBox, width: "100%", color: "#fff", textAlign: "center", outline: "none" };
const quickLine = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 5 };
const autoInput = { width: 88, minHeight: 36, borderRadius: 14, border: "1px solid rgba(255,255,255,.09)", background: "#17181c", color: "#fff", textAlign: "center", fontWeight: 800 };
const autoOptionsStrip = {
  gridColumn: "1 / 3",
  display: "grid",
  gridTemplateColumns: "minmax(0, .85fr) minmax(0, 1.15fr) 92px",
  gap: 8,
  alignItems: "center",
  minHeight: 42,
  margin: "0 -7px -7px",
  padding: "7px 10px",
  borderTop: "1px solid rgba(255,255,255,.06)",
  background: "rgba(0,0,0,.18)",
  borderBottomLeftRadius: 20,
  borderBottomRightRadius: 20,
};
const autoSwitchLabel = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  color: "#b5bac4",
  fontSize: 13,
  fontWeight: 800,
};
const switchInput = {
  appearance: "none",
  WebkitAppearance: "none",
  width: 42,
  height: 24,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.08)",
  background: "radial-gradient(circle at 11px 50%, #4a4d53 0 8px, transparent 9px), #111214",
  boxShadow: "inset 0 2px 4px rgba(0,0,0,.55)",
  transition: "background .16s ease",
  cursor: "pointer",
};
const autoCashoutPill = {
  display: "grid",
  gridTemplateColumns: "1fr 24px",
  alignItems: "center",
  borderRadius: 999,
  background: "#111214",
};
const autoClearButton = {
  border: "none",
  background: "transparent",
  color: "#a9afba",
  fontSize: 20,
  fontWeight: 900,
};
function liveBetTag(canCashout, canCancel) {
  const active = canCashout || canCancel;
  return {
    width: "100%",
    marginTop: 8,
    minHeight: 34,
    border: `1px solid ${canCashout ? "rgba(34,197,94,.45)" : canCancel ? "rgba(255,217,112,.36)" : "rgba(255,255,255,.08)"}`,
    borderRadius: 999,
    background: canCashout ? "rgba(34,197,94,.14)" : canCancel ? "rgba(247,196,75,.12)" : "rgba(255,255,255,.04)",
    color: canCashout ? "#7dffb4" : "#ffd970",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "6px 10px",
    opacity: active ? 1 : .82,
  };
}
const recentBetsCard = {
  marginTop: 14,
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 22,
  background: "linear-gradient(180deg,#121316,#0c0d10)",
  padding: 14,
  boxShadow: "0 18px 55px rgba(0,0,0,.32)",
};
const betTabs = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 4,
  padding: 4,
  background: "#101113",
  borderRadius: 999,
  marginBottom: 8,
};
function betTabButton(active) {
  return {
    minHeight: 42,
    border: "none",
    borderRadius: 999,
    background: active ? "#2e3036" : "transparent",
    color: active ? "#fff" : "#a9b3c3",
    fontSize: 16,
    fontWeight: active ? 800 : 600,
  };
}
const roundResultBox = {
  display: "grid",
  placeItems: "center",
  minHeight: 104,
  marginBottom: 12,
  borderRadius: 18,
  background: "#121315",
  border: "1px solid rgba(255,255,255,.05)",
  color: "#c9d2dd",
  fontSize: 14,
};
const roundResultValue = {
  color: "#f018ff",
  fontSize: 42,
  lineHeight: 1,
  fontWeight: 900,
};
const allBetsSummary = {
  display: "grid",
  gridTemplateColumns: "auto 1fr auto",
  gridTemplateRows: "auto 5px",
  alignItems: "center",
  gap: "4px 10px",
  padding: "8px 10px 10px",
  marginBottom: 12,
  borderRadius: 14,
  background: "#101113",
  border: "1px solid rgba(255,255,255,.05)",
};
const summaryAvatars = { display: "flex", alignItems: "center" };
const summaryCount = { color: "#8f95a3", fontSize: 13 };
const summaryWin = { display: "grid", gap: 2, textAlign: "right", color: "#8f95a3", fontSize: 12 };
const summaryBar = {
  gridColumn: "1 / 4",
  height: 5,
  borderRadius: 999,
  overflow: "hidden",
  background: "#202225",
};
const summaryBarFill = {
  display: "block",
  height: "100%",
  borderRadius: 999,
  background: "#58b817",
};
const topFilterGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 8,
  margin: "8px 0 12px",
};
function topFilterButton(active) {
  return {
    minHeight: 46,
    border: "none",
    borderRadius: 18,
    background: active ? "#2e3036" : "#101113",
    color: active ? "#fff" : "#9fa6b2",
    fontSize: 16,
    fontWeight: active ? 800 : 600,
  };
}
const topHistoryList = { display: "grid", gap: 8 };
const topHistoryCard = {
  display: "grid",
  gridTemplateColumns: "46px 1fr auto",
  gap: 12,
  alignItems: "center",
  borderRadius: 18,
  background: "#101113",
  border: "1px solid rgba(255,255,255,.05)",
  padding: 12,
};
const topHistoryMain = {
  display: "grid",
  gap: 3,
  color: "#9ca3af",
  fontSize: 13,
};
const topHistoryResult = {
  display: "grid",
  gap: 3,
  textAlign: "right",
  color: "#8f95a3",
  fontSize: 13,
};
const recentBetsTable = {
  display: "grid",
  gap: 8,
  maxHeight: 520,
  overflowY: "auto",
  paddingRight: 2,
  scrollbarWidth: "none",
};
const recentBetsTableHead = {
  display: "grid",
  gridTemplateColumns: "1.25fr .75fr .55fr .75fr",
  gap: 10,
  color: "#8d95a3",
  fontSize: 12,
  padding: "0 4px",
};
function recentBetRow(status) {
  const won = String(status || "").toLowerCase() === "won";
  return {
    display: "grid",
    gridTemplateColumns: "1.25fr .75fr .55fr .75fr",
    gap: 10,
    alignItems: "center",
    background: won ? "rgba(37, 92, 8, .55)" : "#101113",
    border: "1px solid rgba(255,255,255,.04)",
    borderRadius: 18,
    padding: "8px 12px",
    fontSize: 13,
  };
}
const recentBetPlayerWrap = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
};
function recentBetAvatar(index) {
  const palette = [
    "linear-gradient(135deg, #ef4444, #f97316)",
    "linear-gradient(135deg, #22c55e, #14b8a6)",
    "linear-gradient(135deg, #3b82f6, #8b5cf6)",
    "linear-gradient(135deg, #eab308, #f59e0b)",
  ];
  return {
    width: 30,
    height: 30,
    flex: "0 0 30px",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: 11,
    fontWeight: 900,
    background: palette[index % palette.length],
    boxShadow: "0 6px 16px rgba(0,0,0,.28)",
  };
}
const recentBetPlayer = {
  color: "#f9fafb",
  fontWeight: 700,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
function recentBetStatus(status) {
  const normalized = String(status || "pending").toLowerCase();
  return {
    color: normalized === "won" ? "#b842ff" : normalized === "lost" ? "#ff7788" : normalized === "cancelled" ? "#ffd970" : "#77c5ff",
    fontWeight: 800,
    textTransform: "capitalize",
    textAlign: "right",
  };
}
function recentBetWin(status) {
  const won = String(status || "").toLowerCase() === "won";
  return {
    color: won ? "#d1d5db" : "#8f95a3",
    fontWeight: won ? 800 : 700,
    textAlign: "right",
  };
}
function resolveAssetUrl(value, fallback) {
  const raw = String(value || fallback || "").trim();
  if (raw.startsWith("/uploads/")) return getMediaUrl(raw);
  return raw || fallback;
}
const emptyFeed = {
  padding: "18px 12px",
  borderRadius: 14,
  background: "rgba(255,255,255,.03)",
  border: "1px solid rgba(255,255,255,.05)",
  color: "#9ca3af",
  textAlign: "center",
  fontSize: 13,
};
const messageBox = { marginTop: 14, minHeight: 44, padding: 12, color: "#d8d8d8", background: "rgba(255,255,255,.055)", border: "1px solid rgba(255,255,255,.09)", borderRadius: 14, fontSize: 13, lineHeight: 1.45 };

export default Aviator;
